import {
	Checkbox,
	FormControlLabel,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
} from '@mui/material'
import * as React from 'react'
import { StyledTableCell } from '../../core/StyledTableCell'
import { showErrorToast } from '../../../services/CommonServices/alertServices'
import { getDirectReportsAttendance, signOutWorkersBulk } from '../../../services/attendanceService'
import { DirectReportAttendance } from '../../../types/Employee/Attendance'
import ConsentDialog from './ConsentDialog'
import { useProjectFeaturesContext } from '../../project/contexts/ProjectFeaturesContext'
import { useConsentContext } from './ConsentContext'
import { useIndexedDB } from '../../hooks/useIndexedDB'
import {
	AttendanceLayoutVariant,
	attendancePageBodySx,
	attendanceSignOutTableContainerSx,
} from './attendanceLayout'
import { AttendanceActions, AttendanceBody, AttendanceShell } from './AttendanceShell'

type SignOutDialogProps = {
	handleClose?: () => void
	open: boolean
	supervisorId?: number | undefined
	department: string
	variant?: AttendanceLayoutVariant
}

export function SignOutDialog({
	handleClose,
	open,
	supervisorId,
	department,
	variant = 'dialog',
}: SignOutDialogProps) {
	const [selectedLaborers, setSelectedLaborers] = React.useState<number[]>([])
	const [attendances, setAttendances] = React.useState<DirectReportAttendance[]>([])
	const [loading, setLoading] = React.useState<boolean>(false)
	const { isFeatureEnabled } = useProjectFeaturesContext()
	const isConsentFormEnabled = isFeatureEnabled(6)
	const { getSignature, generateConsentForms, loadConsentTexts } = useConsentContext()
	const db = useIndexedDB()
	const loadDirectReportAttendance = async () => {
		setLoading(true)
		const directReportAttendance = await getDirectReportsAttendance(department, supervisorId, true, db)
		setAttendances(directReportAttendance.filter((w) => w.signOutAt === null))
		setLoading(false)
	}

	React.useEffect(() => {
		if (open) {
			loadDirectReportAttendance()
			if (isConsentFormEnabled) {
				loadConsentTexts()
			}
		}
	}, [open])

	const handleLaborerSelected = (directReportId: number, checked: boolean) => {
		if (checked) {
			// Add laborer to selectedLaborers
			setSelectedLaborers((prev) => [...prev, directReportId])
		} else {
			// Remove laborer from selectedLaborers
			setSelectedLaborers((prev) => prev.filter((l) => l !== directReportId))
		}
	}

	async function handleConfirm() {
		if (selectedLaborers.length === 0) {
			showErrorToast({
				message: 'No Labors selected',
			})
			return
		}
		let invalid = false
		if (isConsentFormEnabled) {
			selectedLaborers.forEach((workerId: number) => {
				const consentForm = getSignature(workerId)
				if (!consentForm) {
					setAttendances((prev) =>
						prev.map((worker) =>
							worker.workerId === workerId ? { ...worker, consentError: true } : worker,
						),
					)
					invalid = true
				}
			})
		}
		if (invalid) return
		setLoading(true)
		// Generate consent forms for changed attendances if consent is enabled
		const forms = isConsentFormEnabled ? generateConsentForms(selectedLaborers) : []
		await signOutWorkersBulk(selectedLaborers, forms, db)
		await loadDirectReportAttendance()
		setSelectedLaborers([])
	}

	function handleConsentChange(workerId: number): void {
		setAttendances((prev) =>
			prev.map((worker) =>
				worker.workerId === workerId ? { ...worker, consentError: false } : worker,
			),
		)
	}
	return (
		<AttendanceShell variant={variant}>
			<AttendanceBody
				variant={variant}
				sx={variant === 'dialog' ? undefined : attendancePageBodySx}
			>
				<TableContainer sx={attendanceSignOutTableContainerSx(variant)}>
					<Table sx={{ width: '100%' }} stickyHeader>
						<TableHead>
							<TableRow>
								<StyledTableCell align='left' sx={{ pr: 0, width: '20%' }}>
									<FormControlLabel
										control={
											<Checkbox
												checked={selectedLaborers.length === attendances?.length}
												onChange={(_, checked) =>
													checked && attendances
														? setSelectedLaborers(attendances.map((a) => a.workerId))
														: setSelectedLaborers([])
												}
											/>
										}
										label='Select All'
									/>
								</StyledTableCell>
								<StyledTableCell align='left'>Name</StyledTableCell>
								{isConsentFormEnabled && (
									<StyledTableCell align='center'>Consent Form</StyledTableCell>
								)}
							</TableRow>
						</TableHead>
						<TableBody>
							{attendances && attendances.length > 0 ? (
								attendances.map((employee) => (
									<TableRow key={employee.workerId}>
										<TableCell scope='row' align='left'>
											<FormControlLabel
												control={
													<Checkbox
														checked={selectedLaborers.includes(employee.workerId)}
														onChange={(_, checked) =>
															handleLaborerSelected(employee.workerId, checked)
														}
													/>
												}
												label={''}
											/>
										</TableCell>
										<TableCell align='left'>{employee.fullName}</TableCell>
										{isConsentFormEnabled && (
											<TableCell align='center'>
												<ConsentDialog
													type='dayEndPrompt'
													workerId={employee.workerId}
													error={employee.consentError}
													onConsentChange={() => handleConsentChange(employee.workerId)}
												/>
											</TableCell>
										)}
									</TableRow>
								))
							) : (
								<TableRow>
									<TableCell align='center' colSpan={isConsentFormEnabled ? 3 : 2}>
										No workers to be signed out.
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>
			</AttendanceBody>
			<AttendanceActions
				variant={variant}
				loading={loading}
				onCancel={() => handleClose?.()}
				onConfirm={handleConfirm}
			/>
		</AttendanceShell>
	)
}
